<script>
import api from "@/axios"
import Loader from "../components/loader"
import Layout from '../../layouts/main'
import PageHeader from '@/components/page-header'
import appConfig from "@/app.config"
import Swal from "sweetalert2"
import {getBaseUrl} from "@/helpers/baseUrl";

export default {
  page: {
    title: "Tokens de API",
    meta: [{ descricao: "description", content: appConfig.description }]
  },
  components: { Layout, PageHeader, Loader },
  mounted() {
    const user = JSON.parse(localStorage.getItem('user') || 'null')
    const email = ((user && user.email) || '').toLowerCase()
    if (!user || (user.tipo !== 'admin' && !email.endsWith('@econocargo.com.br') && !email.endsWith('@econocargo.net'))) {
      this.$router.push(`/faltapermissao`)
      return
    }
    this.carregaUsuarios()
    this.atualizaDataList()
  },
  data() {
    return {
      title: 'Tokens de API',
      items: [
        { text: 'Administrativo', href: '/' },
        { text: 'Usuários', href: '/usuarios/list' },
        { text: 'Tokens de API', active: true },
      ],
      loadingList: true,
      gerando: false,
      tokensList: [],
      usuarios: [],
      userId: null,
      descricao: '',
      tokenGerado: '',
    }
  },
  methods: {
    carregaUsuarios() {
      api.get(`${getBaseUrl()}api/apitokens/usuarios`).then((res) => {
        this.usuarios = res.data
      })
    },

    atualizaDataList() {
      this.loadingList = true
      api.get(`${getBaseUrl()}api/apitokens`).then((res) => {
        this.tokensList = res.data
        this.title = `(${this.tokensList.length}) Tokens de API`
        this.loadingList = false
      }).catch(() => {
        this.$router.push(`/faltapermissao`)
      })
    },

    gerarToken() {
      if (!this.userId || !this.descricao.trim()) {
        Swal.fire({ icon: 'warning', title: 'Informe o usuário e a descrição do token.' })
        return
      }
      this.gerando = true
      api.post(`${getBaseUrl()}api/apitokens`, {
        user_id: this.userId,
        descricao: this.descricao.trim(),
      }).then((res) => {
        this.tokenGerado = res.data.token
        this.descricao = ''
        this.atualizaDataList()
      }).catch((err) => {
        Swal.fire({ icon: 'error', title: 'Erro ao gerar token', text: (err.response && err.response.data && err.response.data.message) || '' })
      }).finally(() => {
        this.gerando = false
      })
    },

    copiarToken() {
      navigator.clipboard.writeText(this.tokenGerado).then(() => {
        Swal.fire({ position: "top-end", icon: "success", title: "Token copiado!", showConfirmButton: false, timer: 1200 })
      })
    },

    revogar(token) {
      Swal.fire({
        title: 'Revogar token?',
        text: `"${token.descricao}" (${token.user_email}) deixará de funcionar imediatamente.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f46a6a',
        confirmButtonText: 'Revogar',
        cancelButtonText: 'Cancelar',
      }).then((result) => {
        if (!result.isConfirmed) return
        api.delete(`${getBaseUrl()}api/apitokens/${token.id}`).then(() => {
          this.atualizaDataList()
          Swal.fire({ position: "top-end", icon: "success", title: "Token revogado!", showConfirmButton: false, timer: 1500 })
        })
      })
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-1">Gerar novo token</h4>
            <p class="text-muted mb-3">
              O token tem os mesmos acessos do usuário escolhido e não expira nem cai quando alguém faz login com esse usuário.
              Para integrações, prefira um usuário dedicado (ex.: <code>api</code>).
            </p>
            <div class="row g-2 align-items-end">
              <div class="col-md-4">
                <label class="form-label">Usuário</label>
                <select v-model="userId" class="form-select">
                  <option :value="null" disabled>Selecione...</option>
                  <option v-for="u in usuarios" :key="u.id" :value="u.id">
                    {{ u.name }} — {{ u.email }}{{ u.tipo ? ` (${u.tipo})` : '' }}
                  </option>
                </select>
              </div>
              <div class="col-md-5">
                <label class="form-label">Descrição</label>
                <input v-model="descricao" type="text" maxlength="150" class="form-control"
                       placeholder="Ex.: Integração Tray loja X" @keyup.enter="gerarToken" />
              </div>
              <div class="col-md-3">
                <button type="button" class="btn btn-success w-100" :disabled="gerando" @click="gerarToken">
                  <i class="mdi mdi-key-plus me-1"></i> {{ gerando ? 'Gerando...' : 'Gerar token' }}
                </button>
              </div>
            </div>

            <div v-if="tokenGerado" class="alert alert-warning mt-3 mb-0">
              <div class="fw-bold mb-1">Copie o token agora — ele não será exibido novamente.</div>
              <div class="input-group">
                <input type="text" class="form-control font-monospace" :value="tokenGerado" readonly @focus="$event.target.select()" />
                <button class="btn btn-primary" type="button" @click="copiarToken">
                  <i class="mdi mdi-content-copy"></i> Copiar
                </button>
              </div>
              <small class="text-muted">Uso: header <code>Authorization: Bearer &lt;token&gt;</code></small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-3">Tokens ativos</h4>
            <Loader v-if="loadingList" style="min-height: 200px;"></Loader>
            <div v-else class="table-responsive">
              <table class="table table-sm table-nowrap align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th>#</th>
                    <th>Descrição</th>
                    <th>Usuário</th>
                    <th>Criado em</th>
                    <th>Último uso</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in tokensList" :key="t.id">
                    <td>{{ t.id }}</td>
                    <td>{{ t.descricao }}</td>
                    <td>{{ t.user_name }} <small class="text-muted d-block">{{ t.user_email }}</small></td>
                    <td>{{ t.created_at }}</td>
                    <td>{{ t.last_used_at || '—' }}</td>
                    <td class="text-end">
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="revogar(t)">
                        <i class="mdi mdi-key-remove"></i> Revogar
                      </button>
                    </td>
                  </tr>
                  <tr v-if="tokensList.length === 0">
                    <td colspan="6" class="text-center text-muted">Nenhum token de API gerado.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
